/**
 * ============================================================================
 * SECURITY ALERTS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Display security alerts and notifications
 */

import React, { useState, useEffect } from 'react';
import { useSecurity } from '../../hooks/useSecurity';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import Button from '../common/Button';

/**
 * Security Alerts Component
 * @returns {JSX.Element}
 */
const SecurityAlerts = () => {
  const {
    securityAlerts,
    loadSecurityAlerts,
    markAlertAsRead,
    loading,
  } = useSecurity();
  const { showError } = useToast();
  const { formatDate, formatTime, language } = useI18n();

  useEffect(() => {
    loadSecurityAlerts();
  }, [loadSecurityAlerts]);

  const handleMarkAsRead = async (alertId) => {
    try {
      await markAlertAsRead(alertId);
      await loadSecurityAlerts();
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de mise à jour' : 'Failed to update')
      );
    }
  };

  const getSeverityVariant = (severity) => {
    const variants = {
      low: 'info',
      medium: 'warning',
      high: 'error',
      critical: 'error',
    };
    return variants[severity] || 'default';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'from-blue-500 to-cyan-500',
      medium: 'from-yellow-500 to-orange-500',
      high: 'from-orange-500 to-red-500',
      critical: 'from-red-500 to-pink-500',
    };
    return colors[severity] || 'from-gray-500 to-slate-500';
  };

  const getAlertIcon = (type) => {
    const icons = {
      failed_login: '🔒',
      suspicious_ip: '🌐',
      unauthorized_access: '⚠️',
      new_device: '📱',
      password_change: '🔑',
      security_update: '🛡️',
    };
    return icons[type] || '🔔';
  };

  if (loading && !securityAlerts) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <Spinner size="lg" />
      </Card>
    );
  }

  const unreadAlerts = securityAlerts?.filter((alert) => !alert.read) || [];
  const readAlerts = securityAlerts?.filter((alert) => alert.read) || [];

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🔔</span>
            {language === 'fr' ? 'Alertes de sécurité' : 'Security Alerts'}
          </h3>
          {unreadAlerts.length > 0 && (
            <StatusBadge variant="error" size="sm">
              {unreadAlerts.length} {language === 'fr' ? 'non lues' : 'unread'}
            </StatusBadge>
          )}
        </div>

        {/* Unread Alerts */}
        {unreadAlerts.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">
              {language === 'fr' ? 'Non lues' : 'Unread'}
            </h4>
            {unreadAlerts.map((alert, index) => (
              <Card
                key={alert.id || index}
                variant="outlined"
                className={`bg-gradient-to-r ${getSeverityColor(
                  alert.severity
                )} bg-opacity-10 border-2 border-current animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getSeverityColor(
                      alert.severity
                    )} flex items-center justify-center text-2xl shadow-lg flex-shrink-0`}
                  >
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-black text-gray-900 dark:text-white">
                        {alert.title || alert.type}
                      </h4>
                      <StatusBadge variant={getSeverityVariant(alert.severity)} size="sm">
                        {alert.severity}
                      </StatusBadge>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {alert.message || alert.description}
                    </p>
                    {alert.timestamp && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(alert.timestamp)} {formatTime(alert.timestamp)}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMarkAsRead(alert.id)}
                    className="flex-shrink-0"
                  >
                    ✓
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Read Alerts */}
        {readAlerts.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">
              {language === 'fr' ? 'Lues' : 'Read'}
            </h4>
            {readAlerts.slice(0, 5).map((alert, index) => (
              <Card
                key={alert.id || index}
                variant="outlined"
                className="bg-gray-50 dark:bg-gray-800/50 opacity-75 animate-fadeInUp"
                style={{ animationDelay: `${(unreadAlerts.length + index) * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-400 dark:bg-gray-600 flex items-center justify-center text-xl flex-shrink-0">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-700 dark:text-gray-300 text-sm mb-1">
                      {alert.title || alert.type}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {alert.timestamp && formatDate(alert.timestamp)}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {(!securityAlerts || securityAlerts.length === 0) && (
          <EmptyState
            icon="🔔"
            title={language === 'fr' ? 'Aucune alerte' : 'No alerts'}
            description={
              language === 'fr'
                ? 'Vous n\'avez pas d\'alertes de sécurité'
                : "You don't have any security alerts"
            }
          />
        )}
      </div>
    </Card>
  );
};

export default SecurityAlerts;
